import { useFragment, graphql } from 'react-relay';
import { defineMessages, useIntl } from 'dibs-react-intl';
import { getEditionFromNftName } from 'dibs-nft-item-tile/exports/getEditionFromNftName';
import {
    shortAddress,
    etherscanUrl,
    ipfsGatewayLink,
} from 'dibs-crypto-utils/exports/formattingUtils';
import { type detailHelpers_nftItem$key } from './__generated__/detailHelpers_nftItem.graphql';

const messages = defineMessages({
    tokenName: { id: 'nft.pdpDetails.tokenName', defaultMessage: 'Token' },
    tokenNameUnknown: { id: 'nft.pdpDetails.tokenNameUnknown', defaultMessage: 'Name Unknown' },
    tokenAddress: { id: 'nft.pdpDetails.tokenAddress', defaultMessage: 'Etherscan' },
    tokenId: { id: 'nft.pdpDetails.tokenId', defaultMessage: 'Token ID' },
    tokenStandard: { id: 'nft.pdpDetails.tokenStandard', defaultMessage: 'Token Standard' },
    edition: { id: 'nft.pdpDetails.edition', defaultMessage: 'Edition' },
    medium: { id: 'nft.pdpDetails.medium', defaultMessage: 'Medium' },
    dimensions: { id: 'nft.pdpDetails.dimensions', defaultMessage: 'Dimensions' },
    viewMetadata: { id: 'nft.pdpDetails.viewMetadata', defaultMessage: 'Metadata' },
    viewArtwork: { id: 'nft.pdpDetails.viewArtwork', defaultMessage: 'View Artwork' },
    unavailable: { id: 'nft.pdpDetails.viewunavailableArtwork', defaultMessage: 'Unavailable' },
    web: { id: 'nft.pdpDetails.webLink', defaultMessage: 'web' },
    tokenEditions: {
        id: 'nft.pdpDetails.tokenEditions',
        defaultMessage: 'Edition of {totalSupply}',
    },
});

type PdpDetailType = {
    detailKey: string;
    detailDisplayName?: string | null;
    detailValue?: string | null;
    href?: string | null;
};

function getDims(dims: string | null | undefined): string | undefined {
    if (dims === 'variable') {
        return dims;
    }
    if (dims) {
        return dims.split('x').join(' x ');
    }
    return void 0;
}

export function usePdpDetails({
    nftItem: nftItemRef,
}: {
    nftItem: detailHelpers_nftItem$key;
}): PdpDetailType[][] {
    const intl = useIntl();
    const nftItem = useFragment(
        graphql`
            fragment detailHelpers_nftItem on NftItem {
                tokenId
                tokenUri
                contract {
                    address
                    name
                    tokenContractImport {
                        collectionName
                    }
                }
                metadata {
                    ... on NftDigitalArtMetadata {
                        name
                        medium
                        edition
                        media {
                            url
                            dimensions
                        }
                    }
                }
                totalSupply
                isERC1155
            }
        `,
        nftItemRef
    );
    const { tokenId, tokenUri: _tokenUri, contract, metadata, isERC1155 } = nftItem || {};
    const { address, name, tokenContractImport } = contract || {};
    const { media, medium, name: nftName } = metadata || {};
    const { url, dimensions } = media || {};
    const tokenUri = _tokenUri || '';
    const unavailableText = intl.formatMessage(messages.unavailable);
    const getUriLabel = (uri?: string | null): string => {
        if (!uri) {
            return unavailableText;
        }
        if (/^ipfs/.test(uri)) {
            return 'IPFS';
        } else if (/^https?:\/\/arweave/.test(uri)) {
            return 'arweave';
        } else if (/^data:/.test(uri)) {
            return 'on chain';
        } else {
            return intl.formatMessage(messages.web);
        }
    };
    const getStandardName = (): string => {
        if (isERC1155) {
            return 'ERC-1155';
        } else {
            return 'ERC-721';
        }
    };
    return [
        [
            {
                detailKey: 'token-name',
                detailDisplayName: intl.formatMessage(messages.tokenName),
                detailValue:
                    tokenContractImport?.collectionName ||
                    name ||
                    intl.formatMessage(messages.tokenNameUnknown),
            },
            {
                detailKey: 'token-id',
                detailDisplayName: intl.formatMessage(messages.tokenId),
                detailValue: tokenId || unavailableText,
            },
            {
                detailKey: 'token-standard',
                detailDisplayName: intl.formatMessage(messages.tokenStandard),
                detailValue: getStandardName(),
            },
            {
                detailKey: 'token-edition',
                detailDisplayName: intl.formatMessage(messages.edition),
                detailValue: isERC1155
                    ? intl.formatMessage(messages.tokenEditions, {
                          totalSupply: nftItem?.totalSupply,
                      })
                    : getEditionFromNftName(nftName, metadata?.edition) || '1/1',
            },
            {
                detailKey: 'token-medium',
                detailDisplayName: intl.formatMessage(messages.medium),
                detailValue: medium || unavailableText,
            },
            {
                detailKey: 'token-dimensions',
                detailDisplayName: intl.formatMessage(messages.dimensions),
                detailValue: dimensions ? getDims(dimensions) : unavailableText,
            },
        ],
        [
            {
                detailKey: 'token-address',
                detailDisplayName: intl.formatMessage(messages.tokenAddress),
                detailValue: address ? shortAddress(address) : unavailableText,
                href: address ? etherscanUrl(`/token/${address}`) : undefined,
            },
            {
                detailKey: 'token-uri',
                detailDisplayName: intl.formatMessage(messages.viewMetadata),
                detailValue: getUriLabel(tokenUri),
                href: ipfsGatewayLink(tokenUri || ''),
            },
            {
                detailKey: 'token-ipfs',
                detailDisplayName: getUriLabel(url),
                detailValue: getUriLabel(url),
                href: url ? ipfsGatewayLink(url) : undefined,
            },
        ],
    ];
}
